'use client'

import { type CommonProps } from '@/utils/types'
import { type FC } from 'react'
import cn from '@/utils/classNames'
import Accordion from '@/components/Accordion'
import { motion } from 'framer-motion'

const DistictiveFactorsPressuresSidebar: FC<CommonProps> = ({ className }) => {
  const classList = cn('bg-gray-40 text-yale-700 rounded-md px-5 py-6', className)

  return (
    <motion.div
      initial={{ x: '5%', opacity: 0 }}
      animate={{ x: 0, opacity: 1 }}
      transition={{ duration: 0.25, ease: 'easeIn', delay: 0.5 }}
      className="mr-4 xl:mr-7"
    >
      <div className={classList}>
        <p>
          Maritime transportation encompasses the movement of goods and passengers across oceanic routes,
          serving as a pivotal mode of global transportation.
        </p>
        <p className="pt-6">
          The industry potentially exerts various types of pressures on the marine environment.
        </p>
        <p className="pt-7">
          Such pressures includes:
        </p>
        <Accordion type="single" className="py-2.5 border-t border-white mt-2" collapsible>
          <Accordion.Item value="item-1">
            <Accordion.Trigger>GHG Emissions</Accordion.Trigger>
            <Accordion.Content>
              Emissions are generated by the combustion of fossil fuels in the ship&apos;s machinery.
              These include GHGs as well as other air pollutants: sulphur oxides (SOx), nitrogen oxides (NOx),
              particulate matter (PM) and carbon monoxide (CO).
            </Accordion.Content>
          </Accordion.Item>
          <Accordion.Item value="item-2">
            <Accordion.Trigger>Introduction of marine litter</Accordion.Trigger>
            <Accordion.Content>
              Accumulation of waste used onboard (plastic, metal, glass, and other packing materials,
              organic waste, paper products, cargo residues)
              that could end up in the ocean mainly through illegal or accidental dumping.
            </Accordion.Content>
          </Accordion.Item>
          <Accordion.Item value="item-3">
            <Accordion.Trigger>Collision with marine species</Accordion.Trigger>
            <Accordion.Content>
              Collisions between vessels and marine species, such as whales, dolphins, and sea turtles can happen accidentally.
            </Accordion.Content>
          </Accordion.Item>
          <Accordion.Item value="item-4">
            <Accordion.Trigger>Introduction of energy</Accordion.Trigger>
            <Accordion.Content>
              The sector is a primary source of underwater noise, mainly from propellers.
              Introduction of light, electromagnetic radiation, radio waves and vibrations are also associated with the sector.
            </Accordion.Content>
          </Accordion.Item>
          <Accordion.Item value="item-5">
            <Accordion.Trigger>Oil spills</Accordion.Trigger>
            <Accordion.Content>
              Oil spills from marine transportation occur when vessels carrying oil or oil-based products experience accidents,
              leaks, or spills while at sea.
            </Accordion.Content>
          </Accordion.Item>
          <Accordion.Item value="item-6">
            <Accordion.Trigger>Introduction of non-indigenous species</Accordion.Trigger>
            <Accordion.Content>
              The two main vectors for the introduction of NIS are ballast waters and biofouling
            </Accordion.Content>
          </Accordion.Item>
          <Accordion.Item value="item-7">
            <Accordion.Trigger>Impact on seafloor integrity</Accordion.Trigger>
            <Accordion.Content>
              Damage to the seafloor may occur when ships lie at anchor while waiting to access port facilities.
            </Accordion.Content>
          </Accordion.Item>
          <Accordion.Item value="item-8">
            <Accordion.Trigger>Introduction of contaminants</Accordion.Trigger>
            <Accordion.Content>
              Use of antifouling paints applied to the underwater hulls of ships to prevent the accumulation of marine organisms.
              Discharge of grey waters, sewage and bilge waters that contain oil, hazardous metals and chemicals.
            </Accordion.Content>
          </Accordion.Item>
        </Accordion>
      </div>
    </motion.div>
  )
}

export default DistictiveFactorsPressuresSidebar
